import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Loader2, Search, Trash2, Save } from "lucide-react";

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: search, limit: 50 } });
      setItems(data.items);
      setTotal(data.total);
      const next = {};
      data.items.forEach((u) => {
        next[u.id] = { name: u.name, role: u.role, email: u.email };
      });
      setEdits(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const saveUser = async (id) => {
    try {
      await api.patch(`/admin/users/${id}`, edits[id]);
      toast.success("Utilisateur mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="glass rounded-lg p-4 sm:p-6">
      <form
        className="flex gap-2 mb-6"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(q.trim());
        }}
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]" size={16} />
          <Input
            className="pl-9 bg-[color:var(--surface)] border-[color:var(--border)]"
            placeholder="Rechercher par nom ou email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Button type="submit" className="btn-gold">Chercher</Button>
      </form>

      <p className="text-xs text-[color:var(--text-secondary)] mb-4">{total} utilisateur(s)</p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[color:var(--primary)]" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[color:var(--text-secondary)] border-b border-[color:var(--border)]">
                <th className="py-2 pr-3">Nom</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Rôle</th>
                <th className="py-2 pr-3">ELO</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-[color:var(--border)]/50">
                  <td className="py-3 pr-3">
                    <Input
                      className="h-8 bg-[color:var(--surface)] border-[color:var(--border)]"
                      value={edits[u.id]?.name ?? ""}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [u.id]: { ...prev[u.id], name: e.target.value } }))}
                    />
                  </td>
                  <td className="py-3 pr-3">
                    <Input
                      className="h-8 bg-[color:var(--surface)] border-[color:var(--border)]"
                      value={edits[u.id]?.email ?? ""}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [u.id]: { ...prev[u.id], email: e.target.value } }))}
                    />
                  </td>
                  <td className="py-3 pr-3">
                    <select
                      className="h-8 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-2 text-sm"
                      value={edits[u.id]?.role ?? "user"}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [u.id]: { ...prev[u.id], role: e.target.value } }))}
                    >
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td className="py-3 pr-3 text-[color:var(--primary)] font-semibold">{u.stats?.elo ?? 1000}</td>
                  <td className="py-3 flex gap-2">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => saveUser(u.id)}>
                      <Save size={14} className="mr-1" /> OK
                    </Button>
                    <Button size="sm" variant="destructive" className="h-8" onClick={() => deleteUser(u.id, u.name)}>
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
